import type {
  SankeyLayoutLink,
  SankeyLayoutModel,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';
import type { SankeyShape } from '../types/sankey-shape.js';
import { findConnectedShapes } from './find-connected-shapes.js';
import { resolveLayoutLink, resolveLayoutNode } from './layout-index.js';

/**
 * The set of nodes and links that should be visually emphasized in the active
 * interaction state. All other nodes and links are dimmed.
 */
export interface ActiveConnectedSet {
  nodes: SankeyLayoutNode[];
  links: SankeyLayoutLink[];
}

function resolveNodeSet(node: SankeyLayoutNode): ActiveConnectedSet {
  const connected = findConnectedShapes({ type: 'node', node });
  const relatedNodes = [...connected.nodes].filter((n) => n !== node);
  return { nodes: [node, ...relatedNodes], links: [...connected.links] };
}

/**
 * The click-selection set:
 * - **node**: the node, its directly connected links, and the opposite-end
 *   nodes of those links (1-hop neighbors).
 * - **link**: the link and its two endpoint nodes.
 *
 * Returns `undefined` when the selected identity is not present in the current
 * layout (e.g. a data change dropped it).
 */
function resolveSelectedSet(
  layout: SankeyLayoutModel,
  selected: SankeyShape,
): ActiveConnectedSet | undefined {
  if (selected.type === 'node') {
    const node = resolveLayoutNode(layout, selected.nodeId);
    if (!node) {
      return undefined;
    }
    return resolveNodeSet(node);
  }

  const resolved = resolveLayoutLink(
    layout,
    selected.sourceId,
    selected.targetId,
    selected.linkIndex,
  );
  if (!resolved) {
    return undefined;
  }
  const connected = findConnectedShapes({ type: 'link', ...resolved });
  return { nodes: [...connected.nodes], links: [...connected.links] };
}

/**
 * The hover set (1-hop for both variants):
 * - **node**: the node, its directly connected links, and the opposite-end
 *   nodes of those links.
 * - **link**: the link, its source, and its target.
 *
 * Returns `undefined` when the hovered identity is not present in the current
 * layout.
 */
function resolveHoveredSet(
  layout: SankeyLayoutModel,
  hovered: SankeyShape,
): ActiveConnectedSet | undefined {
  if (hovered.type === 'node') {
    const node = resolveLayoutNode(layout, hovered.nodeId);
    if (!node) {
      return undefined;
    }
    return resolveNodeSet(node);
  }

  const resolved = resolveLayoutLink(
    layout,
    hovered.sourceId,
    hovered.targetId,
    hovered.linkIndex,
  );
  if (!resolved) {
    return undefined;
  }
  return {
    nodes: [resolved.source, resolved.target],
    links: [resolved.link],
  };
}

/**
 * Resolves the active visual connected set from the current click-selection and
 * hover state, applying click-over-hover precedence.
 *
 * Click selection always takes visual precedence: when `selected` is defined
 * and its identity resolves in the current `layout`, `hovered` is ignored for
 * visual rendering purposes. When the selected identity is no longer present
 * (data changed), the function falls through to hover state so the pointer can
 * recover visually without requiring an explicit deselect. With neither
 * interaction it returns `undefined` (no dimming).
 *
 * Both `selected` and `hovered` carry only stable ids (not layout-object
 * references) so this function always reads from the current layout, never
 * stale geometry.
 */
export function getActiveConnectedSet(
  layout: SankeyLayoutModel,
  selected: SankeyShape | undefined,
  hovered: SankeyShape | undefined,
): ActiveConnectedSet | undefined {
  const selectedSet = selected
    ? resolveSelectedSet(layout, selected)
    : undefined;
  if (selectedSet) {
    return selectedSet;
  }

  return hovered ? resolveHoveredSet(layout, hovered) : undefined;
}
